import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === 3 ? 0 : prev + 1));
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openReservation = () => {
    window.open(
      'https://www.handsos.com/home/common/etcreserve/etcHompy_reserve.asp?pkC=12557986',
      '_blank'
    );
  };

  return (
    <div className="min-h-screen bg-[#FDFCFA]">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 flex items-center justify-center text-[#C4A574]">
                <i className="ri-hotel-bed-line text-3xl"></i>
              </div>
              <span className="text-xl font-bold tracking-tight text-gray-900">
                프리미엄 클린
              </span>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('services')}
                className="text-sm font-medium transition-colors cursor-pointer text-gray-700 hover:text-[#C4A574]"
              >
                서비스 상세
              </button>
              <button
                onClick={() => scrollToSection('why-us')}
                className="text-sm font-medium transition-colors cursor-pointer text-gray-700 hover:text-[#C4A574]"
              >
                왜 우리인가
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="text-sm font-medium transition-colors cursor-pointer text-gray-700 hover:text-[#C4A574]"
              >
                이용 요금
              </button>
              <button
                onClick={() => scrollToSection('service-info')}
                className="text-sm font-medium transition-colors cursor-pointer text-gray-700 hover:text-[#C4A574]"
              >
                이용 안내
              </button>
              <button
                onClick={() => scrollToSection('faq')}
                className="text-sm font-medium transition-colors cursor-pointer text-gray-700 hover:text-[#C4A574]"
              >
                자주 묻는 질문
              </button>
            </nav>
            <button
              onClick={openReservation}
              className="hidden md:block px-6 py-2.5 bg-[#C4A574] text-white rounded-lg text-sm font-semibold hover:bg-[#B39564] transition-colors whitespace-nowrap cursor-pointer"
            >
              지금 바로 인력 요청
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            alt="점주의 마음으로 완성하는 객실"
            className="w-full h-full object-cover object-top"
            src="https://public.readdy.ai/ai/img_res/597fd8f924afbf670f925a45caf950a0.jpg"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 text-center max-w-5xl mx-auto px-6 w-full">
          <h1 className="text-4xl md:text-7xl font-bold text-white mb-6 md:mb-8 leading-tight tracking-tight">
            보이지 않는 구석까지
            <br />
            정돈된 공간,
            <br />
            <span className="text-[#E8D4B0]">점주의 마음으로 완성</span>합니다
          </h1>
          <p className="text-lg md:text-2xl text-white/95 mb-8 md:mb-12 font-light leading-relaxed">
            구미·김천 숙박업 현장을 누구보다 잘 아는 베테랑이 모였습니다.
            <br className="hidden md:block" />
            펑크 없는 인력, 완벽한 객실 정비로 운영의 여유를 드립니다.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={openReservation}
              className="w-full sm:w-auto px-10 py-4 bg-white text-gray-900 rounded-lg text-base font-bold hover:bg-gray-50 transition-all shadow-xl whitespace-nowrap cursor-pointer"
            >
              지금 바로 인력 요청하기
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="w-full sm:w-auto px-10 py-4 bg-[#C4A574] text-white rounded-lg text-base font-bold hover:bg-[#B39564] transition-all shadow-xl whitespace-nowrap cursor-pointer"
            >
              서비스 상세 보기
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-32 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-20">
            <p className="text-xs uppercase tracking-widest text-[#C4A574] mb-4 font-semibold">
              PRICING &amp; PROCESS
            </p>
            <h2 className="text-3xl md:text-6xl font-bold text-gray-900 leading-tight mb-4 md:mb-6">
              이용 요금표
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              객실 타입과 옵션에 따른
              <br />
              투명하고 합리적인 요금 체계
            </p>
          </div>

          {/* 메인 이미지 */}
          <div className="max-w-5xl mx-auto mb-12 md:mb-16">
            <div className="w-full h-64 md:h-96 rounded-3xl overflow-hidden shadow-2xl">
              <img
                alt="프리미엄 객실 정비 서비스"
                className="w-full h-full object-cover object-top"
                src="https://readdy.ai/api/search-image?query=perfectly%20organized%20luxury%20hotel%20room%20interior%20showing%20meticulous%20cleaning%20service%2C%20crisp%20white%20linens%20with%20sharp%20corner%20folds%20on%20bed%2C%20spotless%20bathroom%20visible%20in%20background%2C%20everything%20in%20designated%20place%2C%20warm%20white%20lighting%20creating%20welcoming%20ambiance%2C%20minimalist%20aesthetic%20with%20beige%20and%20white%20tones%2C%20professional%20hospitality%20cleaning%20standards%20demonstrating%20premium%20service%20quality%2C%20sophisticated%20neutral%20color%20palette%20pristine%20clean%20space&width=1200&height=600&seq=pricing-main-image&orientation=landscape"
              />
            </div>
          </div>

          {/* 기본 요금 */}
          <div className="max-w-5xl mx-auto mb-12 md:mb-16">
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    기본 이용 금액
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base">1PC / 개별 욕실 포함</p>
                </div>
                <div className="text-right">
                  <div className="flex items-baseline">
                    <span className="text-4xl md:text-5xl font-bold text-[#C4A574]">
                      6,000
                    </span>
                    <span className="text-xl md:text-2xl text-gray-600 ml-2">원</span>
                  </div>
                  <p className="text-gray-500 text-sm mt-1">일반 객실 (기본형)</p>
                </div>
              </div>
            </div>
          </div>

          {/* 객실 타입 업그레이드 */}
          <div className="max-w-5xl mx-auto mb-12 md:mb-16">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
              객실 타입 업그레이드
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-[#C4A574]">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-[#FAF8F5] rounded-xl flex items-center justify-center text-[#C4A574] mr-4">
                    <i className="ri-hotel-bed-line text-2xl"></i>
                  </div>
                  <div className="flex items-baseline">
                    <span className="text-3xl md:text-4xl font-bold text-gray-900">+2,000</span>
                    <span className="text-lg text-gray-600 ml-1">원</span>
                  </div>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">트윈룸 (Twin)</h4>
                <p className="text-gray-600 text-sm md:text-base">침대 2개 구성</p>
              </div>
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-[#C4A574]">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-[#FAF8F5] rounded-xl flex items-center justify-center text-[#C4A574] mr-4">
                    <i className="ri-contrast-drop-line text-2xl"></i>
                  </div>
                  <div className="flex items-baseline">
                    <span className="text-3xl md:text-4xl font-bold text-gray-900">+1,000</span>
                    <span className="text-lg text-gray-600 ml-1">원</span>
                  </div>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">욕조 추가 (Bathtub)</h4>
                <p className="text-gray-600 text-sm md:text-base">개별 욕조 설치 객실</p>
              </div>
            </div>
          </div>

          {/* 스페셜 룸 사양 */}
          <div className="max-w-5xl mx-auto mb-12 md:mb-16">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
              스페셜 룸 사양
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 bg-[#C4A574] rounded-xl flex items-center justify-center text-white mb-4">
                  <i className="ri-computer-line text-2xl"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">2PC 룸</h4>
                <p className="text-gray-600 text-sm md:text-base mb-4">커플 및 게이밍 최적화 사양</p>
                <p className="text-[#C4A574] font-semibold text-sm">차등 적용</p>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 bg-[#C4A574] rounded-xl flex items-center justify-center text-white mb-4">
                  <i className="ri-shirt-line text-2xl"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">스타일러 룸</h4>
                <p className="text-gray-600 text-sm md:text-base mb-4">의류 관리기(스타일러) 구비</p>
                <p className="text-[#C4A574] font-semibold text-sm">차등 적용</p>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 bg-[#C4A574] rounded-xl flex items-center justify-center text-white mb-4">
                  <i className="ri-tv-line text-2xl"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">OTT 룸</h4>
                <p className="text-gray-600 text-sm md:text-base mb-4">넷플릭스 등 OTT 서비스 완비</p>
                <p className="text-[#C4A574] font-semibold text-sm">차등 적용</p>
              </div>
              <div className="bg-gradient-to-br from-[#C4A574] to-[#B39564] rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-white mb-4">
                  <i className="ri-vip-crown-line text-2xl"></i>
                </div>
                <h4 className="text-xl font-bold text-white mb-3">스위트 룸 (Suite)</h4>
                <p className="text-white/90 text-sm md:text-base mb-4">
                  스타일러 + OTT + 2PC + 욕조 등<br />
                  최고급 사양 집약
                </p>
                <p className="text-white font-semibold text-sm">상세 단가 적용</p>
              </div>
            </div>
          </div>

          {/* 맞춤형 추천 조합 */}
          <div className="max-w-5xl mx-auto mb-12 md:mb-16">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
              💡 맞춤형 추천 조합
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-block px-3 py-1 bg-[#FAF8F5] rounded-full text-sm font-semibold text-[#C4A574]">
                    A
                  </span>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">6,000</span>
                    <span className="text-lg text-gray-600 ml-1">원</span>
                  </div>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">알뜰 실속형</h4>
                <p className="text-gray-600 text-sm">일반 객실 (1PC)</p>
              </div>
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-block px-3 py-1 bg-[#FAF8F5] rounded-full text-sm font-semibold text-[#C4A574]">
                    B
                  </span>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">7,000</span>
                    <span className="text-lg text-gray-600 ml-1">원</span>
                  </div>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">힐링 스파형</h4>
                <p className="text-gray-600 text-sm">일반 객실 + 욕조 추가</p>
              </div>
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-block px-3 py-1 bg-[#FAF8F5] rounded-full text-sm font-semibold text-[#C4A574]">
                    C
                  </span>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">8,000</span>
                    <span className="text-lg text-gray-600 ml-1">원</span>
                  </div>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">비즈니스 트윈형</h4>
                <p className="text-gray-600 text-sm">트윈룸 + 1PC</p>
              </div>
              <div className="bg-gradient-to-br from-[#C4A574] to-[#B39564] rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm font-semibold text-white">
                    D
                  </span>
                  <div className="flex items-baseline">
                    <span className="text-2xl font-bold text-white">상세 단가</span>
                  </div>
                </div>
                <h4 className="text-lg font-bold text-white mb-2">프리미엄 풀옵션</h4>
                <p className="text-white/90 text-sm">
                  스위트/OTT룸<br />
                  (스타일러/2PC/욕조 포함)
                </p>
              </div>
            </div>
          </div>

          {/* 프리미엄 하루청소 패키지 */}
          <div className="max-w-5xl mx-auto mb-12 md:mb-16">
            <div className="bg-gradient-to-br from-[#2C2C2C] to-[#1a1a1a] rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#C4A574]/10 rounded-full -mr-32 -mt-32"></div>
              <div className="relative z-10">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8">
                  <div className="mb-6 md:mb-0">
                    <span className="inline-block px-4 py-2 bg-[#C4A574] rounded-full text-sm font-semibold text-white mb-4">
                      패키지 상품
                    </span>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                      프리미엄 하루청소 패키지
                    </h3>
                    <p className="text-white/80 text-sm md:text-base">14개 객실 기준</p>
                  </div>
                  <div className="text-left md:text-right">
                    <div className="flex items-baseline mb-2">
                      <span className="text-4xl md:text-5xl font-bold text-[#C4A574]">
                        80,000
                      </span>
                      <span className="text-xl md:text-2xl text-white/90 ml-2">원</span>
                    </div>
                    <p className="text-white/70 text-sm">+1객실당 7,000원</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="flex items-start">
                    <i className="ri-information-line text-[#C4A574] text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <p className="text-white/90 text-sm md:text-base leading-relaxed">
                      추가 청소구역(복도, 계단 등)은 별도 문의 바랍니다
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={openReservation}
              className="inline-flex items-center justify-center px-10 md:px-12 py-4 md:py-5 bg-[#C4A574] text-white rounded-xl text-base md:text-lg font-bold hover:bg-[#B39564] transition-all shadow-xl whitespace-nowrap cursor-pointer"
            >
              상세 견적 문의하기
            </button>
          </div>
          <p className="text-center text-gray-500 text-xs md:text-sm mt-8 md:mt-12 leading-relaxed">
            * 모든 요금은 부가세 별도이며, 세금계산서 발행 가능합니다
            <br />
            * 정기 계약 시 추가 할인 혜택이 제공됩니다
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-20">
            <p className="text-xs uppercase tracking-widest text-[#C4A574] mb-4 font-semibold">
              SERVICE DETAIL
            </p>
            <h2 className="text-3xl md:text-6xl font-bold text-gray-900 leading-tight mb-4 md:mb-6">
              한정된 공간의 미학
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              말씀하신 정비 항목을 중심으로
              <br />
              완벽한 객실 정비를 약속드립니다
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 max-w-6xl mx-auto">
            <div className="bg-[#FAF8F5] rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300 group cursor-pointer">
              <div className="w-full h-64 md:h-80">
                <img
                  alt="칼주름 베드 메이킹"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  src="https://readdy.ai/api/search-image?query=perfectly%20made%20luxury%20hotel%20bed%20with%20crisp%20white%20linens%20showing%20sharp%20knife-edge%20corner%20folds%20and%20smooth%20surface%2C%20warm%20white%20lighting%20creating%20soft%20shadows%2C%20luxury%20hospitality%20standards%2C%20minimalist%20aesthetic%20with%20beige%20and%20white%20tones%2C%20high-end%20hotel%20room%20ambiance%2C%20professional%20bed%20making%20detail%20with%20knife-edge%20precision%2C%20pristine%20clean%20bedding%2C%20sophisticated%20neutral%20color%20palette%20attention%20to%20detail%20in%20hospitality%20cleaning&width=800&height=800&seq=service-bedmaking&orientation=squarish"
                />
              </div>
              <div className="p-6 md:p-10">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-5">
                  완벽한 휴식의 시작
                </h3>
                <p className="text-sm md:text-base font-semibold text-[#C4A574] mb-3 md:mb-4">
                  침구 관리
                </p>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  사용된 시트 전량 교체는 기본,
                  <br />
                  칼주름 베드 메이킹으로 객실의 첫인상을 결정합니다.
                </p>
              </div>
            </div>
            <div className="bg-[#FAF8F5] rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300 group cursor-pointer">
              <div className="w-full h-64 md:h-80">
                <img
                  alt="욕실 청결"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  src="https://readdy.ai/api/search-image?query=spotless%20gleaming%20hotel%20bathroom%20showing%20chrome%20faucet%20and%20mirror%20without%20any%20water%20marks%2C%20perfectly%20polished%20shiny%20surfaces%2C%20warm%20white%20lighting%20demonstrating%20pristine%20cleanliness%2C%20minimalist%20beige%20and%20white%20bathroom%2C%20luxury%20hotel%20standards%2C%20professional%20cleaning%20quality%20sparkling%20clean%20fixtures%20under%20bright%20daylight%20color%20temperature%20lighting%2C%20high-end%20hospitality%20hygiene%20standards&width=800&height=800&seq=service-bathroom&orientation=squarish"
                />
              </div>
              <div className="p-6 md:p-10">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-5">
                  안심할 수 있는 위생
                </h3>
                <p className="text-sm md:text-base font-semibold text-[#C4A574] mb-3 md:mb-4">
                  욕실 / 바닥
                </p>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  물기 없는 수전과 거울, 머리카락 제로 시스템.
                  <br />
                  주광색 조명 아래 반짝이는 청결함을 약속합니다.
                </p>
              </div>
            </div>
            {/* Hidden cards for larger screens */}
            <div className="hidden md:block bg-[#FAF8F5] rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300 group cursor-pointer">
              <div className="w-full h-80">
                <img
                  alt="비품 정렬"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  src="https://readdy.ai/api/search-image?query=perfectly%20aligned%20hotel%20room%20amenities%20and%20supplies%20showing%20meticulous%20organization%2C%20remote%20controls%2C%20paper%20cups%20and%20robes%20precisely%20arranged%20in%20designated%20positions%2C%20warm%20white%20lighting%20minimalist%20beige%20and%20white%20tones%2C%20professional%20hospitality%20standards%2C%20high-end%20hotel%20quality%20everything%20in%20its%20proper%20place%20demonstrating%20attention%20to%20detail%2C%20making%20limited%20space%20look%20spacious%20and%20comfortable%2C%20sophisticated%20arrangement&width=800&height=800&seq=service-amenities&orientation=squarish"
                />
              </div>
              <div className="p-10">
                <h3 className="text-2xl font-bold text-gray-900 mb-5">디테일의 완성</h3>
                <p className="text-base font-semibold text-[#C4A574] mb-4">
                  비품 / 어메니티
                </p>
                <p className="text-gray-600 text-base leading-relaxed">
                  리모컨, 종이컵, 가운 하나까지 지정된 위치에 정렬.
                  <br />
                  한정된 공간을 가장 넓고 쾌적하게 보이도록 세팅합니다.
                </p>
              </div>
            </div>
            <div className="hidden md:block bg-[#FAF8F5] rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300 group cursor-pointer">
              <div className="w-full h-80">
                <img
                  alt="냉장고 정돈"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  src="https://readdy.ai/api/search-image?query=perfectly%20organized%20hotel%20room%20mini%20refrigerator%20interior%20with%20beverage%20bottles%20labels%20facing%20forward%2C%20neatly%20arranged%20items%2C%20clean%20white%20shelves%20minimalist%20aesthetic%20warm%20white%20lighting%20professional%20hospitality%20cleaning%20standards%20showing%20meticulous%20attention%20to%20detail%20spotless%20organized%20space%20high-end%20hotel%20quality%20every%20single%20label%20perfectly%20aligned&width=800&height=800&seq=service-minibar&orientation=squarish"
                />
              </div>
              <div className="p-10">
                <h3 className="text-2xl font-bold text-gray-900 mb-5">보이지 않는 곳의 청결</h3>
                <p className="text-base font-semibold text-[#C4A574] mb-4">
                  냉장고 / 미니바
                </p>
                <p className="text-gray-600 text-base leading-relaxed">
                  음료 라벨 하나까지 정면을 향하도록 정돈하여
                  <br />
                  고객에게 대접받는 느낌을 선사합니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Pricing Section */}
      <section className="py-16 md:py-32 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-20">
            <p className="text-xs uppercase tracking-widest text-[#C4A574] mb-4 font-semibold">
              SERVICE PRICING
            </p>
            <h2 className="text-3xl md:text-6xl font-bold text-gray-900 leading-tight mb-4 md:mb-6">
              서비스 요금
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              투명하고 합리적인 요금 체계로
              <br />
              최상의 서비스를 제공합니다
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto mb-12">
            {/* 기본 출동 */}
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-[#C4A574]">
              <div className="w-16 h-16 bg-[#FAF8F5] rounded-2xl flex items-center justify-center text-[#C4A574] mb-6 mx-auto">
                <i className="ri-service-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">기본 출동</h3>
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-[#C4A574]">100,000</span>
                  <span className="text-2xl text-gray-600 ml-2">원</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm text-center leading-relaxed">
                일반적인 객실 정비 및<br />청소 서비스
              </p>
            </div>

            {/* 긴급 출동 */}
            <div className="bg-gradient-to-br from-[#C4A574] to-[#B39564] rounded-3xl p-8 md:p-10 shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center text-white mb-6 mx-auto">
                <i className="ri-alarm-warning-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 text-center">긴급 출동</h3>
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-white">150,000</span>
                  <span className="text-2xl text-white/90 ml-2">원</span>
                </div>
              </div>
              <p className="text-white/90 text-sm text-center leading-relaxed">
                긴급 상황 대응 및<br />즉시 출동 서비스
              </p>
            </div>

            {/* 대형 작업 */}
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-[#C4A574]">
              <div className="w-16 h-16 bg-[#FAF8F5] rounded-2xl flex items-center justify-center text-[#C4A574] mb-6 mx-auto">
                <i className="ri-building-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">대형 작업</h3>
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-[#C4A574]">200,000</span>
                  <span className="text-2xl text-gray-600 ml-2">원</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm text-center leading-relaxed">
                대규모 청소 및<br />특수 작업 서비스
              </p>
            </div>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 max-w-4xl mx-auto border border-gray-200 mb-10">
            <div className="flex items-start space-x-3">
              <i className="ri-information-line text-[#C4A574] text-xl flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                ※ 작업 범위 및 현장 상황에 따라 추가 비용이 발생할 수 있습니다
              </p>
            </div>
          </div>

          {/* 결제 페이지 이동 버튼 */}
          <div className="text-center">
            <button
              onClick={() => navigate('/payment')}
              className="inline-flex items-center justify-center space-x-3 px-10 md:px-14 py-4 md:py-5 bg-gray-900 text-white rounded-xl text-base md:text-lg font-bold hover:bg-gray-700 transition-all shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-bank-card-line text-xl md:text-2xl"></i>
              <span>서비스 결제하기</span>
            </button>
            <p className="text-gray-500 text-sm mt-4">서비스 이용 후 결제 페이지에서 결제를 진행해 주세요</p>
          </div>
        </div>
      </section>

      {/* Detail Slider Section */}
      <section className="hidden md:block py-24 md:py-32 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-[#C4A574] mb-4 font-semibold">
              ATTENTION TO DETAIL
            </p>
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">
              한정된 공간의
              <br />
              정밀 정돈
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              보이지 않는 곳까지 완벽하게 관리하는 디테일
            </p>
          </div>
          <div className="relative max-w-5xl mx-auto">
            <div className="relative w-full h-[500px] md:h-[600px] rounded-3xl overflow-hidden shadow-2xl">
              {[
                {
                  src: 'https://readdy.ai/api/search-image?query=perfectly%20made%20hotel%20bed%20with%20crisp%20white%20linens%20showing%20precise%20sharp%20corner%20folds%20and%20smooth%20surface%2C%20warm%20white%20lighting%20creating%20soft%20shadows%2C%20luxury%20hospitality%20standards%2C%20minimalist%20aesthetic%2C%20high-end%20hotel%20room%20ambiance%2C%20professional%20bed%20making%20detail%20with%20knife-edge%20precision%2C%20pristine%20clean%20bedding%2C%20sophisticated%20neutral%20beige%20and%20white%20tones%2C%20attention%20to%20detail%20in%20hospitality%20cleaning&width=800&height=600&seq=detail-bed-precision&orientation=landscape',
                  label: '칼주름 베드 메이킹',
                },
                {
                  src: 'https://readdy.ai/api/search-image?query=perfectly%20aligned%20hotel%20room%20amenities%20and%20supplies%20on%20shelf%2C%20remote%20controls%20and%20items%20precisely%20arranged%20in%20designated%20positions%2C%20warm%20white%20lighting%20minimalist%20beige%20tones%2C%20professional%20hospitality%20organization%20high-end%20hotel%20standards%20organized%20arrangement%20showing%20attention%20to%20detail%2C%20everything%20in%20its%20proper%20place&width=800&height=600&seq=detail-alignment&orientation=landscape',
                  label: '지정 위치 정렬',
                },
                {
                  src: 'https://readdy.ai/api/search-image?query=gleaming%20chrome%20bathroom%20faucet%20and%20mirror%20spotless%20without%20water%20marks%2C%20perfectly%20polished%20shiny%20surface%2C%20warm%20white%20lighting%20minimalist%20beige%20bathroom%20background%20luxury%20hotel%20bathroom%20detail%20professional%20cleaning%20quality%20showing%20zero%20water%20stains%20pristine%20polished%20fixture%20high-end%20hospitality%20standards%20sparkling%20clean&width=800&height=600&seq=detail-bathroom-shine&orientation=landscape',
                  label: '물기 없는 수전과 거울',
                },
                {
                  src: 'https://readdy.ai/api/search-image?query=perfectly%20organized%20hotel%20room%20mini%20refrigerator%20interior%20with%20beverage%20bottles%20labels%20facing%20forward%2C%20neatly%20arranged%20items%2C%20clean%20white%20shelves%20minimalist%20aesthetic%20warm%20white%20lighting%20professional%20hospitality%20cleaning%20standards%20showing%20meticulous%20attention%20to%20detail%20spotless%20organized%20space%20high-end%20hotel%20quality%20every%20single%20label%20perfectly%20aligned&width=800&height=600&seq=detail-minibar&orientation=landscape',
                  label: '라벨까지 정면 정돈',
                },
              ].map((slide, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    currentSlide === index ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img alt={slide.label} className="w-full h-full object-cover object-top" src={slide.src} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                    <h3 className="text-3xl md:text-4xl font-bold text-white">{slide.label}</h3>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center space-x-3 mt-8">
              {[0, 1, 2, 3].map((index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`transition-all cursor-pointer rounded-full hover:bg-gray-400 ${
                    currentSlide === index ? 'w-12 h-3 bg-[#C4A574]' : 'w-3 h-3 bg-gray-300'
                  }`}
                  aria-label={`슬라이드 ${index + 1}`}
                ></button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section id="why-us" className="py-16 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-20">
            <p className="text-xs uppercase tracking-widest text-[#C4A574] mb-4 font-semibold">
              WHY CHOOSE US
            </p>
            <h2 className="text-3xl md:text-6xl font-bold text-gray-900 leading-tight mb-4 md:mb-6">
              왜 우리인가?
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              같은 사장님으로서 공감하는 따뜻함과
              <br />
              현장을 아는 전문가의 신뢰를 함께 드립니다
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {[
              {
                icon: 'ri-user-heart-line',
                title: '현직 점주의 노하우',
                desc: '직접 모텔을 운영하며 겪은 고충을 담아,\n점주님이 신경 쓰지 않아도\n알아서 움직이는 인력을 파견합니다.',
              },
              {
                icon: 'ri-map-pin-line',
                title: '구미·김천 지역 특화',
                desc: '우리 지역의 생리를 잘 알기에,\n갑작스러운 인력 공백에도\n가장 빠르게 대응할 수 있습니다.',
              },
              {
                icon: 'ri-checkbox-multiple-line',
                title: '체계적인 자가 검수',
                desc: "작업자 스스로\n'실수 방지 체크리스트'를 준수하여\n컴플레인 없는 정비를 지향합니다.",
              },
            ].map((item, idx) => (
              <div key={idx} className="text-center group">
                <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-6 md:mb-8 bg-[#FAF8F5] rounded-2xl flex items-center justify-center text-[#C4A574] group-hover:bg-[#C4A574] group-hover:text-white transition-all duration-300">
                  <i className={`${item.icon} text-3xl md:text-4xl`}></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-5">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed max-w-sm mx-auto whitespace-pre-line">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-32 bg-[#FAF8F5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-20">
            <p className="text-xs uppercase tracking-widest text-[#C4A574] mb-4 font-semibold">
              FAQ
            </p>
            <h2 className="text-3xl md:text-6xl font-bold text-gray-900 leading-tight mb-4 md:mb-6">
              자주 묻는 질문
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              점주님들이 가장 궁금해하시는 내용을
              <br />
              미리 확인해보세요
            </p>
          </div>
          <div className="space-y-4">
            {[
              {
                question: '오늘 신청하면 내일 바로 인력 투입이 가능한가요?',
                answer:
                  '네, 가능합니다. 구미·김천 지역의 품부한 인력 풀을 보유하고 있어, 전날 오후 6시 이전까지 예약하시면 익일 즉시 배정이 가능합니다.',
              },
              {
                question: '작업자가 갑자기 못 나오게 되면 어떻게 되나요?',
                answer:
                  "저희는 '무단결근 제로'를 목표로 합니다. 비상 대기 인력 시스템을 통해 펑크 발생 시 즉시 대체 인력을 수급하여 점주님의 운영에 차질이 없도록 책임집니다.",
              },
              {
                question: '정해진 청소 범위 외에 추가 요청도 가능한가요?',
                answer:
                  '기본적인 객실 정비 외에 에어컨 필터 청소, 카페트 세척 등은 별도 옵션으로 상담 후 진행 가능합니다. 현장 상황에 맞춰 유연하게 대응해 드립니다.',
              },
              {
                question: '결제는 어떻게 하나요?',
                answer:
                  '사전 상담을 통해 서비스 내용과 금액을 안내드린 후, 확정된 고정 금액에 대해 결제가 진행됩니다. 세금계산서 발행도 100% 지원합니다.',
              },
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-lg"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 md:px-8 py-5 md:py-6 flex items-center justify-between text-left cursor-pointer"
                >
                  <span className="text-base md:text-lg font-bold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-[#C4A574] transition-transform duration-300 flex-shrink-0 ${
                      openFaqIndex === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaqIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 md:px-8 pb-5 md:pb-6">
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Info / Payment / Refund Section */}
      <section id="service-info" className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-xs uppercase tracking-widest text-[#C4A574] mb-4 font-semibold">
              SERVICE GUIDE
            </p>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight mb-4">
              이용 안내
            </h2>
            <p className="text-base text-gray-600 leading-relaxed">
              서비스 예약부터 결제, 환불까지 투명하게 안내드립니다
            </p>
          </div>

          <div className="space-y-6">
            {/* 서비스 제공 및 예약 안내 */}
            <div className="bg-[#FAF8F5] rounded-2xl p-8 md:p-10 border border-gray-100">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl text-[#C4A574] shadow-sm">
                  <i className="ri-calendar-check-line text-xl"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                  서비스 제공 및 예약 안내
                </h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C4A574] mt-2.5 flex-shrink-0"></span>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                    본 서비스는 <strong>모텔·숙박업소 대상 청소 파출 인력 용역 서비스</strong>입니다.
                  </p>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C4A574] mt-2.5 flex-shrink-0"></span>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                    서비스는 제공일 기준 <strong>최대 7일 전까지 예약 가능</strong>하며, 결제 완료 후 확정된 일정에 따라 당일 또는 1일 이내 서비스가 제공됩니다.
                  </p>
                </li>
              </ul>
            </div>

            {/* 결제 안내 */}
            <div className="bg-[#FAF8F5] rounded-2xl p-8 md:p-10 border border-gray-100">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl text-[#C4A574] shadow-sm">
                  <i className="ri-secure-payment-line text-xl"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">결제 안내</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C4A574] mt-2.5 flex-shrink-0"></span>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                    본 서비스는 <strong>사전 상담을 통해 서비스 내용과 금액을 안내드린 후</strong>, 확정된 고정 금액에 대해서만 결제가 진행됩니다.
                  </p>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C4A574] mt-2.5 flex-shrink-0"></span>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                    결제는 토스페이먼츠 PG를 통해 안전하게 진행됩니다.
                  </p>
                </li>
              </ul>
            </div>

            {/* 취소 및 환불 규정 */}
            <div className="bg-[#FAF8F5] rounded-2xl p-8 md:p-10 border border-gray-100">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl text-[#C4A574] shadow-sm">
                  <i className="ri-refund-2-line text-xl"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">취소 및 환불 규정</h3>
              </div>
              <ol className="space-y-4">
                {[
                  '서비스 제공일 전일 18시 이전 취소 시 전액 환불됩니다.',
                  '서비스 제공일 당일 취소 또는 인력 배정 완료 이후에는 환불이 제한될 수 있습니다.',
                  '당사 사정으로 서비스 제공이 불가능한 경우 전액 환불됩니다.',
                  '환불 관련 사항은 결제 전 사전 안내되며, 고객 동의 후 결제가 진행됩니다.',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <span className="w-6 h-6 rounded-full bg-white text-[#C4A574] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm border border-gray-100">
                      {idx + 1}
                    </span>
                    <p className="text-gray-700 text-sm md:text-base leading-relaxed">{item}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-32 bg-[#FFF9F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-20">
            <p className="text-xs uppercase tracking-widest text-[#C4A574] mb-4 font-semibold">
              TESTIMONIALS
            </p>
            <h2 className="text-3xl md:text-6xl font-bold text-gray-900 leading-tight mb-4 md:mb-6">
              함께하는 사장님들의 목소리
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              구미·김천 지역 숙박업주님들이
              <br />
              직접 경험하고 느낀 진심 어린 후기입니다
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                img: 'https://readdy.ai/api/search-image?query=modern%20clean%20motel%20building%20exterior%20with%20warm%20beige%20and%20brown%20tones%2C%20well-maintained%20facade%20showing%20professional%20hospitality%20standards%2C%20welcoming%20entrance%20with%20clear%20signage%2C%20daytime%20natural%20lighting%2C%20contemporary%20Korean%20motel%20architecture%2C%20neat%20and%20organized%20appearance%20demonstrating%20quality%20management&width=600&height=400&seq=testimonial-motel-1&orientation=landscape',
                alt: '구미 로얄모텔 후기',
                review:
                  '"직원이 갑자기 그만두면서 정말 막막했는데, 프리미엄 클린 덕분에 하루 만에 해결됐어요. 인력 공백 걱정 없이 운영할 수 있어서 정말 감사합니다."',
                name: '김영수 대표',
                place: '구미 로얄모텔',
                region: '구미',
                date: '2024.12',
              },
              {
                img: 'https://readdy.ai/api/search-image?query=perfectly%20organized%20hotel%20room%20interior%20with%20crisp%20white%20bedding%20showing%20knife-edge%20precision%20folds%2C%20warm%20beige%20and%20white%20color%20scheme%2C%20spotless%20clean%20space%20with%20everything%20in%20designated%20place%2C%20professional%20hospitality%20cleaning%20standards%2C%20comfortable%20and%20inviting%20atmosphere%2C%20minimalist%20aesthetic%20demonstrating%20attention%20to%20detail&width=600&height=400&seq=testimonial-room-1&orientation=landscape',
                alt: '김천 프라임호텔 후기',
                review:
                  '"청소 디테일이 정말 놀라웠습니다. 냉장고 음료 라벨까지 정면으로 정렬해주시고, 수전에 물기 하나 없이 반짝반짝하더라고요. 고객 컴플레인이 확 줄었어요."',
                name: '박미경 대표',
                place: '김천 프라임호텔',
                region: '김천',
                date: '2025.01',
              },
              {
                img: 'https://readdy.ai/api/search-image?query=contemporary%20motel%20building%20with%20modern%20design%2C%20clean%20exterior%20showing%20professional%20maintenance%2C%20warm%20neutral%20tones%2C%20well-lit%20entrance%20area%2C%20modern%20Korean%20hospitality%20architecture%2C%20clean%20and%20organized%20appearance%20demonstrating%20quality%20standards&width=600&height=400&seq=testimonial-motel-2&orientation=landscape',
                alt: '구미 스카이모텔 후기',
                review:
                  '"같은 사장 입장에서 뭘 신경 써야 하는지 딱 아시더라고요. 말 안 해도 알아서 척척 해주시니까 제가 다른 일에 집중할 수 있어서 좋습니다."',
                name: '이정훈 대표',
                place: '구미 스카이모텔',
                region: '구미',
                date: '2024.11',
              },
            ].map((t, idx) => (
              <div key={idx} className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group">
                <div className="w-full h-48 md:h-56 overflow-hidden">
                  <img
                    alt={t.alt}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    src={t.img}
                  />
                </div>
                <div className="p-6 md:p-8">
                  <div className="flex items-center space-x-1 mb-4 md:mb-6">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-[#FFB800] text-lg md:text-xl"></i>
                    ))}
                  </div>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-6 md:mb-8">
                    {t.review}
                  </p>
                  <div className="flex items-center justify-between pt-4 md:pt-6 border-t border-gray-100">
                    <div>
                      <p className="text-gray-900 font-bold text-sm md:text-base mb-1">{t.name}</p>
                      <p className="text-gray-600 text-sm">{t.place}</p>
                    </div>
                    <div className="text-right">
                      <span className="inline-block px-3 py-1 bg-[#FAF8F5] rounded-full text-xs font-semibold text-[#C4A574] mb-2">
                        {t.region}
                      </span>
                      <p className="text-gray-400 text-xs">{t.date}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 md:mt-16 text-center">
            <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base leading-relaxed">
              <strong className="text-gray-900">당신도 이런 경험을 하실 수 있습니다</strong>
              <br />
              지금 바로 인력 요청을 시작하세요
            </p>
            <button
              onClick={openReservation}
              className="inline-flex items-center justify-center px-8 md:px-10 py-4 md:py-5 bg-[#C4A574] text-white rounded-xl text-base md:text-lg font-bold hover:bg-[#B39564] transition-all shadow-xl whitespace-nowrap cursor-pointer"
            >
              인력 예약하기
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-32 bg-[#FAF8F5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-widest text-[#C4A574] mb-4 font-semibold">
              CONTACT US
            </p>
            <h2 className="text-3xl md:text-6xl font-bold text-gray-900 leading-tight mb-4 md:mb-6">
              간편 상담 신청
            </h2>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed">
              구미·김천 지역 숙박업 전문 정비 서비스
              <br />
              지금 바로 전화로 상담을 받아보세요
            </p>
          </div>
          <div className="bg-white rounded-3xl p-6 md:p-12 shadow-xl">
            <div className="text-center">
              <a
                href="tel:010-2133-5998"
                className="inline-flex items-center justify-center px-10 md:px-12 py-5 md:py-6 bg-gray-900 text-white rounded-xl text-base md:text-lg font-bold hover:bg-gray-800 transition-all shadow-xl whitespace-nowrap cursor-pointer space-x-3"
              >
                <i className="ri-phone-line text-2xl md:text-3xl"></i>
                <span>010-2133-5998</span>
              </a>
              <p className="text-gray-600 text-sm mt-6">24시간 열려 있습니다</p>
            </div>
          </div>
          <div className="mt-8 md:mt-12 text-center">
            <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base leading-relaxed">
              또는 온라인으로 인력 요청하기
            </p>
            <button
              onClick={openReservation}
              className="inline-flex items-center justify-center px-10 md:px-12 py-4 md:py-5 bg-[#C4A574] text-white rounded-xl text-base md:text-lg font-bold hover:bg-[#B39564] transition-all shadow-xl whitespace-nowrap cursor-pointer space-x-3"
            >
              <i className="ri-calendar-check-line text-xl md:text-2xl"></i>
              <span>온라인 인력 요청</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2C2C2C] py-12 md:py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-8 md:mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-4 md:mb-6">
                <div className="w-10 h-10 flex items-center justify-center text-[#C4A574]">
                  <i className="ri-hotel-bed-line text-3xl"></i>
                </div>
                <span className="text-xl font-bold text-white">프리미엄 클린</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                구미·김천 지역 숙박업 전문
                <br />
                객실 정비 서비스
                <br />
                점주의 마음으로 완성합니다
              </p>
            </div>
            <div>
              <h4 className="text-white text-base font-bold mb-4 md:mb-6">서비스 지역</h4>
              <ul className="space-y-2 md:space-y-3">
                <li className="text-gray-400 text-sm">구미시 전역</li>
                <li className="text-gray-400 text-sm">김천시 전역</li>
                <li className="text-gray-400 text-sm">인근 지역 협의 가능</li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-base font-bold mb-4 md:mb-6">상담 안내</h4>
              <ul className="space-y-2 md:space-y-3">
                <li className="text-gray-400 text-sm">전화: 010-2133-5998</li>
                <li className="text-gray-400 text-sm">운영: 연중무휴 24시간</li>
                <li className="text-gray-400 text-sm">상담: 오전 9시 - 오후 6시</li>
                <li className="text-gray-400 text-sm">긴급 문의: 24시간 가능</li>
              </ul>
            </div>
          </div>

          {/* 사업자 정보 */}
          <div className="border-t border-gray-700 pt-6 md:pt-8">
            <div className="bg-white/5 rounded-xl p-5 md:p-6 mb-6 text-center">
              <p className="text-gray-400 text-xs leading-relaxed">
                <span className="text-gray-300 font-semibold">상호명:</span> 청파&nbsp;&nbsp;|&nbsp;&nbsp;
                <span className="text-gray-300 font-semibold">대표자:</span> 김우근&nbsp;&nbsp;|&nbsp;&nbsp;
                <span className="text-gray-300 font-semibold">사업자등록번호:</span> 602-15-69885
                <br className="md:hidden" />
                <span className="hidden md:inline">&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                <span className="text-gray-300 font-semibold">사업장 주소:</span> 경상북도 구미시 인동32길 22-9, 4층(진평동)
                <br className="md:hidden" />
                <span className="hidden md:inline">&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                <span className="text-gray-300 font-semibold">전화:</span> 010-2133-5998
              </p>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between">
              <p className="text-gray-500 text-sm mb-4 md:mb-0">
                © 2025 청파 (프리미엄 클린). All rights reserved.
              </p>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 text-sm hover:text-[#C4A574] transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white border-t border-gray-200 p-4 shadow-2xl">
        <div className="flex items-center space-x-3">
          <button
            onClick={openReservation}
            className="flex-1 px-6 py-4 bg-[#C4A574] text-white rounded-xl text-base font-bold hover:bg-[#B39564] transition-all whitespace-nowrap cursor-pointer"
          >
            인력 예약
          </button>
          <a
            href="tel:010-2133-5998"
            className="w-16 h-16 bg-gray-900 text-white rounded-xl flex items-center justify-center hover:bg-gray-800 transition-all cursor-pointer"
          >
            <i className="ri-phone-line text-2xl"></i>
          </a>
        </div>
      </div>
    </div>
  );
}
